import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@babomrs/ui';
import { loginRequest } from '../hooks/msalConfig';
import { FIRM_PHONE_DISPLAY, FIRM_PHONE_HREF } from '../lib/firmContact';

/**
 * cp-sign-in (Story #26228) — the signed-out page and the four account-outcome pages.
 *
 * BR-CP-ACC-008: none of these pages has chrome, an inactivity timer or client data. They are rendered outside
 * ClientAppShell (the only place the idle timer is mounted), and they take nothing from GET /client/me but the
 * outcome itself.
 */

/** RFC 7807 types GET /client/me answers with (PortalUserProblemTypes.cs). The page is chosen by type. */
export const PORTAL_USER_PROBLEM = {
  notFound: 'https://docs.babomrs.com/errors/portal-user-not-found',
  suspended: 'https://docs.babomrs.com/errors/portal-user-suspended',
  resetNeeded: 'https://docs.babomrs.com/errors/portal-signin-reset-needed',
} as const;

/**
 * Why a client could not be loaded (BR-CP-ACC-005). FOUR states.
 *
 * `notFound`, `notActive` and `resetNeeded` are answers from the server about the ACCOUNT — the client should call
 * the firm. `error` means we could not ask the question at all, and the client should simply try again. A failure
 * is never reported as an account answer: that tells a client with a good account to ring their accountant about
 * nothing, and hides a platform outage behind a reassuring message. There is no "pending activation" state: the
 * sign-up gate binds an invited person at their first sign-in, so nobody waits for an activation.
 */
export type PortalLoadFailure = 'notFound' | 'notActive' | 'resetNeeded' | 'error';

/**
 * BR-CP-ACC-005 — which page a failed GET /client/me shows. 404 is Account Not Found; a 403 is an account answer
 * only when its problem type says which one; anything else — another status, an unrecognised 403, no network — is
 * ours and retryable.
 */
export function classifyMeFailure(err: unknown): PortalLoadFailure {
  const { status, type } = (err ?? {}) as { status?: number; type?: string };
  if (status === 404) return 'notFound';
  if (status === 403 && type === PORTAL_USER_PROBLEM.suspended) return 'notActive';
  if (status === 403 && type === PORTAL_USER_PROBLEM.resetNeeded) return 'resetNeeded';
  return 'error';
}

const GATE_HEADING = 'm-0 text-[24px] font-bold text-[var(--text)]';
const GATE_TEXT = 'm-0 max-w-[28rem] text-[var(--text2)]';

/**
 * The firm logo at the prototype's size (`.x-cp-access-signin-main .logo-img`, `.gate .logo-img`: 118 x 64, contained).
 * Decorative beside the "Client Portal" heading would lose the firm's name for a screen reader, so it keeps its alt.
 */
function Logo({ className = '' }: { className?: string }) {
  return <img src="/brand/ba-logo-original.jpg" alt="B&A Accounting Group" className={`h-16 w-[118px] object-contain ${className}`} />;
}

/** The firm's number as a 44 px tel: link (the prototype's `.lnk`), from the one constant the portal uses for it. */
function FirmPhoneLink() {
  return (
    <a href={FIRM_PHONE_HREF} className="ui-lnk inline-flex min-h-11 items-center underline-offset-2 hover:underline">
      {FIRM_PHONE_DISPLAY}
    </a>
  );
}

const STEPS: { title: string; hint: string }[] = [
  { title: 'Enter your email', hint: 'The address your accountant invited.' },
  { title: 'Enter the code we email you', hint: 'No code? Check your junk folder.' },
  { title: 'You’re in', hint: 'The first time only, enter your name.' },
];

/**
 * The signed-out page, presentational — the prototype's `#cp-sign-in` (`.x-cp-access-signin`, Story #28003 #28019):
 * one bordered card, the steps panel on white and the firm's welcome panel on navy; below 900 px the two stack with
 * the welcome panel first. BR-CP-ACC-001: Sign In starts the emailed-code sign-in (MSAL loginRedirect with the shared
 * loginRequest); there is no password. Back To Website goes to the public home.
 */
export function SignInView({ onSignIn, onBack }: { onSignIn: () => void; onBack: () => void }) {
  return (
    <main className="min-h-screen bg-[var(--bg)] p-4 sm:p-6">
      <div className="mx-0 my-2 grid min-h-[70vh] grid-cols-1 overflow-hidden rounded-proto border border-[var(--border)] bg-[var(--surface)] min-[900px]:mx-auto min-[900px]:my-6 min-[900px]:max-w-[1100px] min-[900px]:grid-cols-2">
        <section className="flex flex-col justify-center gap-3 px-4 py-6 min-[900px]:px-12 min-[900px]:pb-10 min-[900px]:pt-12">
          <Logo className="mb-2" />
          <h1 className="m-0 text-[28px] font-bold text-[var(--text)]">Client Portal</h1>
          <p className="m-0 max-w-[32rem] text-[var(--text2)]">
            Sign in with the email address your accountant invited &mdash; we&apos;ll email you a one-time code
            to enter. There is no password. The first time, you&apos;ll confirm your email and enter your name.
          </p>
          <ol aria-label="How signing in works" className="mb-1 mt-2 grid list-none gap-3 p-0">
            {STEPS.map((step, i) => (
              <li key={step.title} className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="grid h-7 flex-[0_0_28px] place-items-center rounded-full bg-[var(--accent-light)] text-[13px] font-semibold text-[var(--nav)]"
                >
                  {i + 1}
                </span>
                <span>
                  <b>{step.title}</b>
                  <br />
                  <span className="text-[13px] text-[var(--text2)]">{step.hint}</span>
                </span>
              </li>
            ))}
          </ol>
          <div className="mt-[14px] flex flex-wrap justify-start gap-2">
            <Button variant="primary" onClick={onSignIn}>
              Sign In
            </Button>
            <Button variant="secondary" onClick={onBack}>
              Back To Website
            </Button>
          </div>
          <p className="m-0 mt-[14px] text-[13px] text-[var(--text2)]">
            Trouble signing in? Call the office on <FirmPhoneLink />.
          </p>
        </section>
        <aside className="order-first flex flex-col justify-center gap-3 bg-[var(--nav)] px-4 py-6 text-white min-[900px]:order-none min-[900px]:p-12">
          <p className="m-0 max-w-[28rem] text-[12px] uppercase tracking-[0.2em] text-white/75">
            B&amp;A Accounting Group · Toowoomba accountants since 1981
          </p>
          <p className="m-0 max-w-[28rem] text-[24px] font-bold leading-[1.2] text-white min-[900px]:text-[32px]">
            Welcome to your client portal
          </p>
          <p className="m-0 max-w-[28rem] text-[17px] text-white/[.88]">
            Sign your returns, answer our questions, send us documents and book appointments — in one secure place.
          </p>
        </aside>
      </div>
    </main>
  );
}

/** The signed-out page wired to MSAL and the router. */
export function SignInRequired() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  return (
    <SignInView
      onSignIn={() => {
        instance.loginRedirect(loginRequest).catch((err) => console.error('Login failed:', err));
      }}
      onBack={() => navigate('/')}
    />
  );
}

/**
 * BR-CP-ACC-005 — the four account-outcome pages. The three account answers send the client to the firm; only the
 * "couldn't load" page offers Try Again, which asks GET /client/me again.
 */
export function AccountOutcomePage({ variant, onRetry }: { variant: PortalLoadFailure; onRetry?: () => void }) {
  // The prototype's `.gate`: logo, a 24 px heading and one line, centred in the top 70% of the page (Story #28003).
  return (
    <main className="bg-[var(--bg)] p-4 sm:p-6">
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-[10px] px-2 py-6 text-center">
        <Logo className="mb-3" />
        {variant === 'notFound' && (
          <>
            <h1 className={GATE_HEADING}>Account Not Found</h1>
            <p className={GATE_TEXT}>Please contact your accountant.</p>
          </>
        )}
        {variant === 'notActive' && (
          <>
            <h1 className={GATE_HEADING}>Your portal access is not active</h1>
            <p className={GATE_TEXT}>Please contact your accountant.</p>
          </>
        )}
        {variant === 'resetNeeded' && (
          <>
            <h1 className={GATE_HEADING}>Contact us to reset your sign-in</h1>
            <p className={GATE_TEXT}>
              Your invitation is linked to a different sign-in. Please call the office on <FirmPhoneLink /> and
              we&apos;ll reset it for you.
            </p>
          </>
        )}
        {variant === 'error' && (
          <>
            {/* Deliberately says nothing about the account: we do not know anything about it. */}
            <h1 className={GATE_HEADING}>We couldn’t load your account</h1>
            <p className={GATE_TEXT}>Something went wrong on our end — your account is fine. Please try again in a moment.</p>
            {onRetry && (
              <div className="mt-[14px] flex flex-wrap justify-center gap-2">
                <Button variant="primary" onClick={onRetry}>
                  Try Again
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
